import React, { useState } from "react";
import { useToast } from "../../context/ToastContext";
import { sendRequest } from "../../utils/api";

interface InviteModalProps {
  show: boolean;
  onClose: () => void;
  // apiEndpoint?: string; // e.g. /users/invite
  title?: string;
}

const InviteModal: React.FC<InviteModalProps> = ({
  show,
  onClose,
  // apiEndpoint = "/users/invite",
  title = "Send Invite Link",
}) => {
  const { showToast } = useToast();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false); // Track input focus
  const [isValid, setIsValid] = useState(true);

  if (!show) return null;

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setTouched(true);
    setIsValid(validateEmail(value) || value === ""); // Empty field = no error yet
  };

  const handleSendInvite = async () => {
    if (!validateEmail(email)) {
      showToast("Please enter a valid email address.", { type: "warning" });
      return;
    }

    try {
      setLoading(true);
      await sendRequest(
        "POST", "/users/send-mail",
        { email, type: "invite" }
      );
      showToast("Invite link sent successfully!", {
        type: "success",
      });
      setEmail("");
      onClose();
    } catch (err: any) {
      console.log(err);
      showToast(
        err.response?.data?.message || "Failed to send invite. Try again.",
        { type: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const emailValid = validateEmail(email);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-96 animate-fadeIn">
        <h2 className="text-lg font-semibold mb-4">{title}</h2>

        <div className="mb-1">
          <input
            type="email"
            placeholder="Enter email address"
            value={email}
            onChange={handleEmailChange}
            onBlur={() => setTouched(true)}
            className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring transition-all ${!isValid && touched
                ? "border-red-500 focus:ring-red-200"
                : "border-gray-300 focus:ring-blue-300"
              }`}
            disabled={loading}
          />
          {/* Inline validation message */}
          {!isValid && touched && (
            <p className="text-red-500 text-sm mt-1 transition-all">
              Please enter a valid email address.
            </p>
          )}
        </div>

        <div className="flex justify-end space-x-3 mt-4">
          <button
            className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 transition-all"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>

          <button
            className={`px-4 py-2 rounded-lg text-white transition-all ${emailValid
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-blue-400 cursor-not-allowed opacity-60"
              }`}
            onClick={handleSendInvite}
            disabled={!emailValid || loading}
          >
            {loading ? "Sending..." : "Send Invite"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteModal;
